import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { CORAL_700, GRAY_900 } from 'constants/styling/theme'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { FC, useEffect, useMemo, useState } from 'react'
import { KeyboardEventKey, Params } from 'constants/misc'

import Box from '@mui/material/Box'
import { DeleteOutlineOutlined } from '@mui/icons-material'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUIInputField } from 'components/common/MUIInputField'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useBkbnSearchParams } from 'utils/hooks/useBkbnSearchParams'
import { useDebouncedMemo } from 'utils/helpers'
import { useGetPropertyBrokers } from 'dataQueries'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

enum FilterType {
  MANAGER = 'MANAGER',
}

const MANAGER_LIST_SIZE_LIMIT = 10

/**
 * @component Properties dashboard filter which contains manager dropdown and search input.
 * 
 * @example
 * <PropertiesDashboardFilter />
 */
export const PropertiesDashboardFilter: FC = () => {

  const { t } = useTranslation(['properties_dashboard'])

  const { userPropertyWorkspaceId } = useUserData()

  const propertyParams = useBkbnSearchParams()

  // MAIN URL PARAMS
  const searchProperty = useMemo(() => propertyParams.get(Params.SEARCH), [propertyParams])
  const brokerId = useMemo(() => propertyParams.get(Params.BROKER_ID), [propertyParams])

  const [selectedManager, setSelectedManager] = useState<string>('')
  const [searchManager, setSearchManager] = useState<string>('')
  const [disableSearchManager, setDisableSearchManager] = useState<boolean>(true)

  const managersParams = useDebouncedMemo(() => {
    return {
      size: MANAGER_LIST_SIZE_LIMIT,
      page: 0,
      query: searchManager ?? '',
    }
  }, [selectedManager], 300)

  const propertyManagersQuery = useGetPropertyBrokers(userPropertyWorkspaceId, managersParams)

  const managersList = useMemo(() => {
    if (propertyManagersQuery.isSuccess) return propertyManagersQuery.data?.data?.content
    return []
  }, [propertyManagersQuery.data?.data?.content, propertyManagersQuery.isSuccess])

  /** Handles the change of the selected manager by updating the state and setting the broker ID parameter.   */
  const handleChangeManager = (name: string, id?: string) => {
    setSelectedManager(name)

    // If the name is not the same as the selected manager, set the search manager to the name
    if (!id && name !== selectedManager) { setSearchManager(name) }

    // Sets the ID of the selected manager. If not provided, an empty string will be used.
    if (id || name === '') {
      propertyParams.set([Params.BROKER_ID, id ?? ''])
    }
  }

  /** Handles the removal of the selected manager by resetting the state and clearing the broker ID parameter.   */
  const handleRemoveManager = () => {
    setSelectedManager('')
    setSearchManager('')
    propertyParams.set([Params.BROKER_ID, ''])
  }

  // When managerList changes sets the selected manager based on the broker ID from the URL parameters.
  useEffect(() => {
    if (brokerId && !selectedManager) {
      const manager = managersList.find(manager => manager.id === brokerId)

      if (manager) setSelectedManager(manager.name)
    }

    // If the length of the managersList is less than 10, it disables the search manager input.
    if (managersList.length > 0 && managersList.length <= MANAGER_LIST_SIZE_LIMIT) {
      setDisableSearchManager(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managersList])

  return (
    <Stack flexDirection="row" justifyContent="space-between" alignContent="center" marginBottom="1.6rem" height="3.6rem">

      <MUIDropdown
        button={(isOpen, action) => (
          <MUIDropdownInput
            readOnly={disableSearchManager}
            value={selectedManager ?? ''}
            onChange={e => handleChangeManager(e.target.value)}
            placeholder={t('manager')}
            type={DropdownInputTypes.SELECTOR}
            onClick={(e) => {
              logAnalyticsEvent(AnalyticsEvent.FILTER_PROPERTY, { filterType: FilterType.MANAGER })
              action(e)
            }}
            customInputContent={!!selectedManager &&
              <Stack
                flexDirection="row"
                paddingRight="1.2rem"
                fontSize="1.4rem"
                alignItems="center"
                paddingTop="0.1rem"
                color={GRAY_900}
                onClick={handleRemoveManager}
                sx={{ cursor: 'pointer', ':hover': { color: CORAL_700 } }}
              >
                <DeleteOutlineOutlined fontSize="inherit" />
              </Stack>
            }
          />
        )}
      >
        <Box width="20.5rem">
          {managersList.map(({ id, name }) =>
            <MUIDropdownItem key={id} onClick={() => handleChangeManager(name, id)}>
              <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                {name}
              </Typography>
            </MUIDropdownItem>
          )}
        </Box>
      </MUIDropdown>

      <MUIInputField
        value={searchProperty ?? ''}
        placeholder={t('property_search_placeholder')}
        onChange={e => {
          propertyParams.set([Params.SEARCH, e.target.value])
        }}
        onKeyDown={e => {
          if (e.key === KeyboardEventKey.ENTER) { e.preventDefault() } // Prevent form submission on enter
        }}
      />

    </Stack>
  )
}
